import { useEffect, useState } from 'react';
import API from '../../lib/api';
import { SETUP_TIMEOUT_MS, withSetupRetry } from '../../lib/setupRequest';
import { MICROSOFT_SIGN_IN_ENABLED } from '../../lib/oauthConfig';
import { accountContinuePath } from '../../lib/shareContinue';
import { encodeSignupProfile, scanDraftFields } from '../../lib/scanSignupDraft';
import { peekPendingContactPath } from '../../lib/openContactReturn';
import { AppleMark, GoogleMark, MicrosoftMark } from './ProviderMarks';

/**
 * "Continue with Google" / "Continue with Microsoft" buttons.
 *
 * Rendered at the top of /login and /signup. Each button is a plain
 * `<a href>` that hits the backend `/api/auth/oauth/{provider}/start`
 * endpoint, which then 302s to the provider — keeping all OAuth state
 * server-side. We probe `/api/auth/oauth/providers` once on mount to
 * disable buttons whose env vars are not configured yet.
 */
export default function OAuthButtons({
  mode = 'signin',
  showDivider = true,
  iosWaitlist = false,
  androidWaitlist = false,
  providerOrder = ['google', 'apple'],
}) {
  const [providers, setProviders] = useState(null);
  const [probeError, setProbeError] = useState(false);
  const [probeAttempt, setProbeAttempt] = useState(0);

  useEffect(() => {
    let cancel = false;
    setProbeError(false);
    withSetupRetry(() => API.get('/auth/oauth/providers', { timeout: SETUP_TIMEOUT_MS }))
      .then(({ data }) => { if (!cancel) setProviders(data); })
      .catch(() => {
        if (cancel) return;
        setProviders({ google: false, microsoft: false, apple: false });
        setProbeError(true);
      });
    return () => { cancel = true; };
  }, [probeAttempt]);

  // Build the start URL with the post-login redirect target.
  const startUrl = (provider) => {
    const redirect = encodeURIComponent(accountContinuePath() || peekPendingContactPath() || '/');
    const base = process.env.REACT_APP_BACKEND_URL || '';
    // Signup sends a list only while its box stays checked. Sign-in from
    // /login sends both as the default for a brand-new account. The
    // new-only flag keeps an existing sign-in off those lists.
    const iosOn = mode === 'signup' ? iosWaitlist : true;
    const androidOn = mode === 'signup' ? androidWaitlist : true;
    const ios = iosOn ? '&ios_waitlist=1' : '';
    const android = androidOn ? '&android_waitlist=1' : '';
    const neu = mode === 'signin' ? '&waitlist_new_only=1' : '';
    const profile = encodeSignupProfile(scanDraftFields());
    const drafted = profile ? `&signup_profile=${encodeURIComponent(profile)}` : '';
    return `${base}/api/auth/oauth/${provider}/start?redirect=${redirect}${ios}${android}${neu}${drafted}`;
  };

  const label = (provider) => {
    // Session 228: unified label. "Sign in with Google" / "Sign up with Google"
    // matches the mobile button.
    const verb = mode === 'signup' ? 'Sign up' : 'Sign in';
    const name = provider === 'google' ? 'Google' : provider === 'microsoft' ? 'Microsoft' : 'Apple';
    return `${verb} with ${name}`;
  };

  const renderBtn = (provider) => {
    const enabled = Boolean(providers?.[provider]);
    const className =
      'w-full h-12 inline-flex items-center justify-center gap-3 rounded-full border ' +
      (enabled
        ? 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
        : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed');
    if (!enabled) {
      return (
        <button
          key={provider}
          type="button"
          disabled
          className={className}
          data-testid={`oauth-${provider}-btn-disabled`}
          aria-disabled="true"
          title="Not configured yet"
        >
          <ProviderIcon provider={provider} muted />
          <span className="font-heading font-semibold text-sm">{label(provider)}</span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">Coming soon</span>
        </button>
      );
    }
    return (
      <a
        key={provider}
        href={startUrl(provider)}
        className={className}
        data-testid={`oauth-${provider}-btn`}
      >
        <ProviderIcon provider={provider} />
        <span className="font-heading font-semibold text-sm">{label(provider)}</span>
      </a>
    );
  };

  const providersToShow = providerOrder.filter((provider) => (
    provider !== 'microsoft' || MICROSOFT_SIGN_IN_ENABLED
  ));
  if (MICROSOFT_SIGN_IN_ENABLED && !providersToShow.includes('microsoft')) {
    providersToShow.push('microsoft');
  }

  return (
    <div className="flex flex-col gap-3" data-testid="oauth-buttons">
      {providersToShow.map((provider) => renderBtn(provider))}
      {probeError ? (
        <button
          type="button"
          className="w-full text-sm font-semibold text-nav-blue"
          data-testid="oauth-providers-retry"
          onClick={() => setProbeAttempt((current) => current + 1)}
        >
          Could not check sign-in options. Try again.
        </button>
      ) : null}
      {showDivider && (
        <div className="flex items-center gap-3 pt-2">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-[10px] uppercase tracking-wider text-slate-400">or continue with email</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
      )}
    </div>
  );
}

function ProviderIcon({ provider, muted }) {
  const fade = muted ? 'opacity-50' : '';
  if (provider === 'google') return <span className={fade}><GoogleMark size={18} /></span>;
  if (provider === 'microsoft') return <span className={fade}><MicrosoftMark size={18} /></span>;
  // The sign-in button is white, so the Apple glyph stays dark on it in
  // every theme. Settings inherits the theme foreground instead.
  return <AppleMark size={18} className={`text-slate-800 ${fade}`} />;
}
